import { DEFAULT_ENV_ORIGIN_MAP, KNOWN_ENVS, KNOWN_ORIGINS, ORIGIN_MAP } from './constants'
import type {
  AllowedEnv,
  TranCopKnownEnv,
  TranCopUploadOrigin,
  TranCopUploadOriginSaaS,
} from './types'

/**
 * 判断 env 是否为开发环境
 *
 * @param env {String} 任意字符串
 * @returns
 */
export function isDevelopment(env: string): boolean {
  return env.includes('dev')
}

/**
 * 判断 env 是否为 UAT 环境
 *
 * @param env {String} 任意字符串
 * @returns
 */
export function isUat(env: string): boolean {
  return env.includes('uat')
}

/**
 * 判断 env 是否为生产环境
 *
 * @param env {String} 任意字符串
 * @returns
 */
export function isProduction(env: string): boolean {
  return env.includes('prod') || env.endsWith('hk')
}

/**
 * 标准化环境变量
 *
 * @param env {String} 环境变量
 * @returns {TranCopKnownEnv}
 */
export function normalizeEnv(env: AllowedEnv): TranCopKnownEnv {
  if (KNOWN_ENVS.includes(env as TranCopKnownEnv)) {
    return env as TranCopKnownEnv
  }

  switch (true) {
    case isProduction(env): {
      return 'prod'
    }
    case isUat(env): {
      return 'uat'
    }
    case isDevelopment(env): {
      return 'dev'
    }
    default: {
      return 'prod' // 默认返回 prod 环境
    }
  }
}

/**
 * 获取上传的 origin
 *
 * @param env {AllowedEnv} 环境变量
 * @param origin {TranCopUploadOrigin} 可选的上传 origin
 * @returns {TranCopUploadOrigin} 返回上传的 origin
 * @throws {TypeError} 如果 origin 不在 ORIGIN_MAP 中
 */
export function getUploadOrigin(
  env: AllowedEnv,
  origin?: TranCopUploadOrigin,
): TranCopUploadOrigin {
  if (origin) {
    if (!(origin in ORIGIN_MAP)) {
      throw new TypeError(`Invalid origin: ${origin}`)
    }

    return origin
  }

  const normalizedEnv = normalizeEnv(env)

  return DEFAULT_ENV_ORIGIN_MAP[normalizedEnv]
}

/**
 * 判断传入的 origin 是否为 saas 环境
 *
 * @param origin {TranCopUploadOrigin} 上传的 origin
 * @returns {boolean} 如果是 saas 环境，则返回 true，否则返回 false
 */
export function isSaaSOrigin(origin: TranCopUploadOrigin): origin is TranCopUploadOriginSaaS {
  return KNOWN_ORIGINS.includes(origin) && origin.startsWith('saas-')
}

/**
 * 获取上传的 origin 的 URL
 *
 * @param origin {TranCopUploadOrigin} 上传的 origin
 * @returns {string} 返回上传的 origin 的 URL
 */
export function getOriginUrl(origin: TranCopUploadOrigin): string {
  return ORIGIN_MAP[origin]
}
